/**
 * Admin → Team: what each staff member and website owner worked on and what it cost, today and this month.
 * Read only. Titles and costs only: no message content is fetched or shown.
 */
import { useEffect, useState } from "preact/hooks";
import { OVERSIGHT_CSS } from "./inbox.js";

type Period = { tasks: number; turns: number; requests: number; cost_usd: number; pkr: number; images: number; last_ts: number };
type Person = { id: string; name: string; email: string; role: "admin" | "staff" | "tenant"; active: boolean; today: Period; month: Period; topModels: Array<{ model: string; requests: number; cost_usd: number; pkr: number }>; lastActive: number | null };
type Task = { chat_id: string; first_ts: number; last_ts: number; cost_usd: number; pkr: number; credits: number; requests: number; turns: number };

async function call<T = any>(path: string): Promise<T> {
  const r = await fetch(`/api${path}`, { headers: { "content-type": "application/json" } });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
  return j;
}

const usd = (n: number) => (n < 0.01 && n > 0 ? `$${n.toFixed(4)}` : `$${n.toFixed(2)}`);
const credits = (n: number) => `${Math.round(n).toLocaleString()} cr`;
const ago = (t: number | null) => {
  if (!t) return "never";
  const m = (Date.now() - t) / 60000;
  return m < 1 ? "just now" : m < 60 ? `${Math.round(m)} min ago` : m < 1440 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} d ago`;
};
const cost = (p: Person, x: { cost_usd: number; pkr: number }) => (p.role === "tenant" ? credits(x.pkr) : usd(x.cost_usd));

function Tasks({ person, onClose }: { person: Person; onClose: () => void }) {
  const [d, setD] = useState<{ tasks: Task[]; titles: Record<string, { title: string; where: string }> } | null>(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    call(`/usage/tasks?staff=${encodeURIComponent(person.id)}`).then(setD).catch((e) => setErr(e.message));
  }, [person.id]);
  return (
    <div style="margin:10px 0 22px">
      <div class="ov-sec"><h3>{person.name}: latest 50 tasks</h3><span><a href="#" onClick={(e) => { e.preventDefault(); onClose(); }}>close</a></span></div>
      <p class="hint">A task is one chat; a turn is one message they sent. Titles of desktop chats stay on their computer, so those show as "Desktop task".</p>
      {err && <div class="error">{err}</div>}
      {!d && !err && <div class="hint">Loading…</div>}
      {d && (
        <div class="ov-scroll">
          <table class="grid ov-tbl">
            <thead><tr><th>Task</th><th>Turns</th><th>Calls</th><th>{person.role === "tenant" ? "Credits" : "Cost"}</th><th>Started</th><th>Last activity</th></tr></thead>
            <tbody>
              {d.tasks.length === 0 && <tr><td colSpan={6} class="hint">No tasks yet.</td></tr>}
              {d.tasks.map((t) => (
                <tr>
                  <td class="wrap">{d.titles[t.chat_id]?.title ?? <span class="hint">Desktop task <code>{t.chat_id.slice(-8)}</code></span>}{d.titles[t.chat_id]?.where === "os" ? <span class="ov-pill" style="margin-left:6px">Cloud OS</span> : null}</td>
                  <td>{t.turns}</td>
                  <td>{t.requests}</td>
                  <td>{person.role === "tenant" ? credits(t.pkr) : usd(t.cost_usd)}</td>
                  <td>{new Date(t.first_ts).toLocaleString()}</td>
                  <td>{ago(t.last_ts)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function TeamView() {
  const [d, setD] = useState<{ people: Person[]; generatedAt: string } | null>(null);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState<Person | null>(null);
  const [showIdle, setShowIdle] = useState(false);
  const load = () => call("/admin/team").then((r) => { setD(r); setErr(""); }).catch((e) => setErr(e.message));
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);
  const people = (d?.people ?? []).filter((p) => showIdle || p.month.requests > 0 || p.today.requests > 0);
  const staff = people.filter((p) => p.role !== "tenant");
  const owners = people.filter((p) => p.role === "tenant");
  const sum = (list: Person[], f: (p: Person) => number) => list.reduce((a, p) => a + f(p), 0);
  const table = (list: Person[], tenant: boolean) => (
    <div class="ov-scroll">
      <table class="grid ov-tbl">
        <thead>
          <tr><th>{tenant ? "Website owner" : "Staff"}</th><th>Today</th><th>This month</th><th>Tasks (month)</th><th>Turns</th><th>Images</th><th>Top models (month)</th><th>Last active</th></tr>
        </thead>
        <tbody>
          {list.length === 0 && <tr><td colSpan={8} class="hint">No activity this month.</td></tr>}
          {list.map((p) => (
            <tr class="ov-person" onClick={() => setOpen(open?.id === p.id ? null : p)} title="Show their tasks">
              <td class="wrap"><b>{p.name}</b> {p.role === "admin" && <span class="ov-pill">admin</span>}{!p.active && <span class="ov-pill warn">disabled</span>}<br /><span class="hint">{p.email}</span></td>
              <td>{cost(p, p.today)}<br /><span class="hint">{p.today.tasks} tasks</span></td>
              <td><b>{cost(p, p.month)}</b></td>
              <td>{p.month.tasks}</td>
              <td>{p.month.turns}</td>
              <td>{p.month.images}</td>
              <td class="wrap">{p.topModels.length ? p.topModels.map((m) => <div>{m.model} <span class="hint">{tenant ? credits(m.pkr) : usd(m.cost_usd)}</span></div>) : <span class="hint">—</span>}</td>
              <td>{ago(p.lastActive)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
  return (
    <div class="admin">
      <style>{OVERSIGHT_CSS}</style>
      <h2>Team</h2>
      <p class="hint">What each person worked on and what it cost. Staff cost is the real USD the company pays; website owners are shown in the credits they are charged (1 credit = PKR 1). Click a person for their task list. Titles and costs only — no message content.</p>
      {err && <div class="error">{err}</div>}
      {!d && !err && <div class="hint">Loading…</div>}
      {d && (
        <>
          <div class="kpis">
            <div class="kpi"><b>{usd(sum(staff, (p) => p.today.cost_usd))}</b><span>Staff today</span></div>
            <div class="kpi"><b>{usd(sum(staff, (p) => p.month.cost_usd))}</b><span>Staff this month</span></div>
            <div class="kpi"><b>{credits(sum(owners, (p) => p.month.pkr))}</b><span>Website owners this month</span></div>
            <div class="kpi"><b>{usd(sum(owners, (p) => p.month.cost_usd))}</b><span>Their real cost this month</span></div>
          </div>
          <label class="hint" style="display:flex;gap:6px;align-items:center;margin:10px 0"><input type="checkbox" checked={showIdle} onChange={(e) => setShowIdle((e.target as HTMLInputElement).checked)} /> Show people with no activity this month</label>
          <div class="ov-sec"><h3>Staff</h3><span>{staff.length} people</span></div>
          {table(staff, false)}
          {open && open.role !== "tenant" && <Tasks person={open} onClose={() => setOpen(null)} />}
          <div class="ov-sec"><h3>Website owners</h3><span>{owners.length} people</span></div>
          {table(owners, true)}
          {open && open.role === "tenant" && <Tasks person={open} onClose={() => setOpen(null)} />}
        </>
      )}
    </div>
  );
}
